import { useState } from 'react';
import { BellRing, LandPlot, Plus, Printer, RefreshCw } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery, useCancelPropertySaleMutation, useCreatePropertyMutation,
  useCreatePropertySaleMutation, usePaymentMethodsQuery,
  useHidePropertySaleMutation, usePropertiesQuery, usePropertySalesQuery,
  useRecordPropertyPaymentMutation, useRestorePropertySaleMutation,
  useRunPropertyRemindersMutation,
  type PropertySale,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney, isoDate } from '@/lib/format';
import {
  Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input,
  LoadingState, Modal, PageHeader, Pagination, Select, Table, Td, Textarea, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { CustomerPicker } from '@/components/CustomerPicker';
import { PropertySaleReceipt } from '@/components/PropertySaleReceipt';
import {
  PropertyFields, emptyPropertyForm, propertyIsComplete, propertyPayload,
  type PropertyFormState,
} from '@/components/PropertyFields';
import { PropertyDetailModal } from './PropertyDetailModal';
import { PropertySaleEditModal } from './PropertySaleEditModal';

const STATUS_TONE: Record<string, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  AVAILABLE: 'success',
  RESERVED: 'warning',
  SOLD: 'brand',
  WITHDRAWN: 'neutral',
  AGREED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

const emptySale = {
  propertyId: '', customerId: '', price: '', accountId: '',
  paymentPlan: 'INSTALMENTS', depositAmount: '', methodId: '', reference: '', notes: '',
  nextDueAt: '',
};

const emptyPayment = { amount: '', methodId: '', accountId: '', reference: '', nextDueAt: '' };

/**
 * Land sales.
 *
 * A property is not stock: one title, sold once, with no quantity and no
 * average cost. What it shares with the rest of the platform is money — every
 * shilling taken here lands in a cash account and shows on the same day-end
 * count as a till payment.
 */
export function PropertySalesPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // Today by default, like the other money screens.
  const [from, setFrom] = useState(isoDate());
  const [to, setTo] = useState(isoDate());
  const [status, setStatus] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [listing, setListing] = useState(false);
  const [selling, setSelling] = useState(false);
  const [paying, setPaying] = useState<PropertySale | null>(null);
  const [cancelling, setCancelling] = useState<PropertySale | null>(null);
  const [printing, setPrinting] = useState<PropertySale | null>(null);
  const [propertyForm, setPropertyForm] = useState<PropertyFormState>(emptyPropertyForm);
  const [viewing, setViewing] = useState<string | null>(null);
  const [editingSale, setEditingSale] = useState<PropertySale | null>(null);
  const [hiding, setHiding] = useState<PropertySale | null>(null);
  const [hideReason, setHideReason] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [saleForm, setSaleForm] = useState(emptySale);
  const [payment, setPayment] = useState(emptyPayment);
  const [reason, setReason] = useState('');

  const sales = usePropertySalesQuery({
    page,
    limit: 25,
    search: search || undefined,
    status: status || undefined,
    from: from || undefined,
    to: to || undefined,
    includeHidden: showHidden || undefined,
  });
  const properties = usePropertiesQuery({ limit: 200 });
  const available = usePropertiesQuery({ limit: 200, availableOnly: true });
  const { data: accounts } = useAccountsQuery({ limit: 50 });
  const { data: methods } = usePaymentMethodsQuery();

  const [createProperty, createPropertyState] = useCreatePropertyMutation();
  const [createSale, createSaleState] = useCreatePropertySaleMutation();
  const [recordPayment, paymentState] = useRecordPropertyPaymentMutation();
  const [cancelSale, cancelState] = useCancelPropertySaleMutation();
  const [runReminders, remindersState] = useRunPropertyRemindersMutation();
  const [hideSale, hideState] = useHidePropertySaleMutation();
  const [restoreSale, restoreState] = useRestorePropertySaleMutation();

  const summary = sales.data?.summary;
  const refreshing = sales.isFetching || properties.isFetching;

  const refresh = (): void => {
    setFailure(null);
    void sales.refetch();
    void properties.refetch();
    void available.refetch();
  };

  const submitProperty = async (): Promise<void> => {
    setFailure(null);
    try {
      await createProperty(propertyPayload(propertyForm, 'create')).unwrap();
      setListing(false);
      setPropertyForm(emptyPropertyForm);
      setNotice('Listed for sale.');
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That property could not be listed'));
    }
  };

  const submitSale = async (): Promise<void> => {
    setFailure(null);
    try {
      const created = await createSale({
        propertyId: saleForm.propertyId,
        customerId: saleForm.customerId,
        price: Number(saleForm.price),
        accountId: saleForm.accountId,
        paymentPlan: saleForm.paymentPlan,
        ...(saleForm.depositAmount
          ? {
              deposit: {
                amount: Number(saleForm.depositAmount),
                ...(saleForm.methodId ? { methodId: saleForm.methodId } : {}),
                ...(saleForm.reference ? { reference: saleForm.reference } : {}),
              },
            }
          : {}),
        ...(saleForm.paymentPlan === 'INSTALMENTS' && saleForm.nextDueAt
          ? { nextDueAt: saleForm.nextDueAt }
          : {}),
        notes: saleForm.notes || undefined,
      }).unwrap();

      setSelling(false);
      setSaleForm(emptySale);
      setNotice(`${created.number} agreed. Print the receipt for the buyer.`);
      setPrinting(created);
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That sale could not be agreed'));
    }
  };

  const confirmHide = async (): Promise<void> => {
    if (!hiding) return;
    setFailure(null);
    try {
      const result = await hideSale({ saleId: hiding.id, reason: hideReason }).unwrap();
      setHiding(null);
      setHideReason('');
      setNotice(
        result.cashEntriesReversed > 0
          ? `Hidden. ${result.cashEntriesReversed} cash entr${
              result.cashEntriesReversed === 1 ? 'y has' : 'ies have'
            } been reversed out of the account.`
          : 'Hidden. No money had been taken against it.',
      );
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That sale could not be hidden'));
    }
  };

  const putBack = async (sale: PropertySale): Promise<void> => {
    setFailure(null);
    try {
      await restoreSale(sale.id).unwrap();
      setNotice(`${sale.number} is back, and so is the money it had taken.`);
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That sale could not be restored'));
    }
  };

  const sendReminders = async (): Promise<void> => {
    setFailure(null);
    setNotice(null);
    try {
      const result = await runReminders().unwrap();
      setNotice(
        result.sales === 0
          ? 'Nothing is due in the next few days, so no reminder was sent.'
          : `Reminded ${result.emails} ${result.emails === 1 ? 'person' : 'people'} about `
            + `${result.sales} ${result.sales === 1 ? 'sale' : 'sales'}.`,
      );
    } catch (error) {
      setFailure(apiErrorMessage(error, 'The reminders could not be sent'));
    }
  };

  const submitPayment = async (): Promise<void> => {
    if (!paying) return;
    setFailure(null);
    try {
      const updated = await recordPayment({
        saleId: paying.id,
        body: {
          amount: Number(payment.amount),
          ...(payment.accountId ? { accountId: payment.accountId } : {}),
          ...(payment.methodId ? { methodId: payment.methodId } : {}),
          ...(payment.reference ? { reference: payment.reference } : {}),
          ...(payment.nextDueAt ? { nextDueAt: payment.nextDueAt } : {}),
        },
      }).unwrap();

      setPaying(null);
      setPayment(emptyPayment);
      setNotice(
        Number(updated.dueAmount) === 0
          ? `${updated.number} is paid in full — the property is now sold.`
          : `Payment taken. ${formatMoney(updated.dueAmount, format)} still owing.`,
      );
      setPrinting(updated);
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That payment could not be recorded'));
    }
  };

  const submitCancel = async (): Promise<void> => {
    if (!cancelling) return;
    setFailure(null);
    try {
      await cancelSale({ saleId: cancelling.id, reason }).unwrap();
      setCancelling(null);
      setReason('');
      setNotice('Cancelled. The property is available again; money already taken stays recorded.');
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That sale could not be cancelled'));
    }
  };

  const selectedProperty = available.data?.items.find((item) => item.id === saleForm.propertyId);

  return (
    <>
      <PageHeader
        title="Property sales"
        description="Land and buildings, the agreements made against them, and the money taken."
        action={
          <div className="flex gap-2">
            <Button variant="outline" icon={RefreshCw} loading={refreshing} onClick={refresh}>
              Refresh
            </Button>
            <Can permission={P.PROPERTY_MANAGE}>
              {/* The sweep runs itself each morning; this is here so the email
                  settings can be proved without waiting for one. */}
              <Button
                variant="outline"
                icon={BellRing}
                loading={remindersState.isLoading}
                onClick={() => void sendReminders()}
              >
                Send reminders
              </Button>
              <Button variant="outline" icon={Plus} onClick={() => setListing(true)}>
                List a property
              </Button>
            </Can>
            <Can permission={P.PROPERTY_SELL}>
              <Button icon={Plus} onClick={() => setSelling(true)}>New sale</Button>
            </Can>
          </div>
        }
      />

      {failure && <div className="mb-4"><Alert tone="danger">{failure}</Alert></div>}
      {notice && <div className="mb-4"><Alert tone="success">{notice}</Alert></div>}

      {summary && (
        <SummaryCards
          stats={[
            { label: 'Sales', value: String(summary.saleCount), hint: 'in this period' },
            { label: 'Agreed', value: formatMoney(summary.agreed, format), hint: 'total price' },
            { label: 'Collected', value: formatMoney(summary.collected, format), hint: 'money in' },
            {
              label: 'Outstanding',
              value: formatMoney(summary.outstanding, format),
              hint: 'still owed on land',
              tone: 'owed',
            },
          ]}
        />
      )}

      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              placeholder="Sale, plot or buyer"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-40">
            <Input
              label="From"
              type="date"
              value={from}
              max={to || isoDate()}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-40">
            <Input
              label="To"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-40">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="AGREED">Agreed</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
          <Can permission={P.PROPERTY_MANAGE}>
            <label className="flex items-center gap-2 pb-2.5 text-sm">
              <input
                type="checkbox"
                className="size-4 rounded border-subtle text-brand-600"
                checked={showHidden}
                onChange={(event) => {
                  setShowHidden(event.target.checked);
                  setPage(1);
                }}
              />
              {/* A deletion nobody can look at is not an audit trail. */}
              Show deleted
            </label>
          </Can>
        </div>

        {sales.isLoading ? (
          <LoadingState />
        ) : sales.isError ? (
          <ErrorState message={apiErrorMessage(sales.error)} onRetry={() => void sales.refetch()} />
        ) : sales.data && sales.data.items.length === 0 ? (
          <EmptyState
            icon={LandPlot}
            title={search || from || status ? 'Nothing matches those filters' : 'No property sales yet'}
            description="List a property, then agree a sale against it."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Sale</Th>
                  <Th>Property</Th>
                  <Th>Buyer</Th>
                  <Th>Agreed</Th>
                  <Th>Next due</Th>
                  <Th numeric>Price</Th>
                  <Th numeric>Paid</Th>
                  <Th numeric>Owing</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={sales.isFetching ? 'opacity-60' : undefined}>
                {sales.data?.items.map((sale) => (
                  <tr
                    key={sale.id}
                    className={sale.deletedAt ? 'opacity-60 hover:bg-sunken' : 'hover:bg-sunken'}
                  >
                    <Td className="font-medium">
                      {sale.number}
                      {sale.deletedAt && (
                        <p className="text-xs font-normal text-muted">
                          hidden{sale.deleteReason ? ` · ${sale.deleteReason}` : ''}
                        </p>
                      )}
                    </Td>
                    <Td>
                      <span className="font-medium">{sale.property.reference}</span>
                      <p className="text-xs text-muted">{sale.property.name}</p>
                    </Td>
                    <Td className="text-secondary">{sale.customer.name}</Td>
                    <Td className="text-secondary">{formatDate(sale.agreedAt, format)}</Td>
                    <Td>
                      <DueDate value={sale.nextDueAt} settled={sale.status !== 'AGREED'} />
                    </Td>
                    <Td numeric>{formatMoney(sale.price, format)}</Td>
                    <Td numeric className="text-secondary">{formatMoney(sale.paidAmount, format)}</Td>
                    <Td
                      numeric
                      className={Number(sale.dueAmount) > 0 ? 'text-red-600 dark:text-red-400' : 'text-secondary'}
                    >
                      {formatMoney(sale.dueAmount, format)}
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[sale.status] ?? 'neutral'}>
                        {sale.status.toLowerCase()}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      <Button size="sm" variant="ghost" icon={Printer} onClick={() => setPrinting(sale)}>
                        Receipt
                      </Button>
                      {sale.deletedAt ? (
                        <Can permission={P.PROPERTY_MANAGE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={restoreState.isLoading}
                            onClick={() => void putBack(sale)}
                          >
                            Restore
                          </Button>
                        </Can>
                      ) : (
                        <>
                          {sale.status !== 'CANCELLED' && (
                            <Can permission={P.PROPERTY_SELL}>
                              <Button size="sm" variant="ghost" onClick={() => setEditingSale(sale)}>
                                Edit
                              </Button>
                            </Can>
                          )}
                          <Can permission={P.PROPERTY_MANAGE}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setHiding(sale);
                                setHideReason('');
                              }}
                            >
                              Delete
                            </Button>
                          </Can>
                        </>
                      )}
                      {sale.status === 'AGREED' && (
                        <Can permission={P.PROPERTY_SELL}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPaying(sale);
                              setPayment({
                                ...emptyPayment,
                                amount: sale.dueAmount,
                                methodId: methods?.find((m) => m.isDefault)?.id ?? '',
                              });
                            }}
                          >
                            Take payment
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setCancelling(sale)}>
                            Cancel
                          </Button>
                        </Can>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {sales.data && (
              <Pagination
                page={sales.data.meta.page}
                totalPages={sales.data.meta.totalPages}
                total={sales.data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Properties"
          description="Every plot on the books, and where each one stands."
        />
        {properties.isLoading ? (
          <LoadingState />
        ) : properties.data?.items.length === 0 ? (
          <EmptyState
            icon={LandPlot}
            title="No properties listed"
            description="Add the land the business has for sale."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Title / plot</Th>
                <Th>Where</Th>
                <Th numeric>Size</Th>
                <Th numeric>Asking</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {properties.data?.items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => setViewing(item.id)}
                  className="cursor-pointer hover:bg-sunken"
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${item.reference}`}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setViewing(item.id);
                    }
                  }}
                >
                  <Td>
                    <span className="font-medium">{item.reference}</span>
                    <p className="text-xs text-muted">{item.name}</p>
                  </Td>
                  <Td className="text-secondary">
                    {item.titleNumber ?? <span className="text-muted">no title on file</span>}
                    {item.plotNumber && (
                      <p className="text-xs text-muted">
                        Plot {item.plotNumber}
                        {item.blockNumber ? ` · Block ${item.blockNumber}` : ''}
                      </p>
                    )}
                  </Td>
                  <Td className="text-secondary">
                    {[item.location, item.district].filter(Boolean).join(', ') || '—'}
                  </Td>
                  <Td numeric className="text-secondary">
                    {item.area ? `${item.area} ${item.areaUnit.toLowerCase().replace('_', ' ')}` : '—'}
                  </Td>
                  <Td numeric className="font-medium">{formatMoney(item.askingPrice, format)}</Td>
                  <Td>
                    <Badge tone={STATUS_TONE[item.status] ?? 'neutral'}>
                      {item.status.toLowerCase()}
                    </Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {listing && (
        <Modal
          open
          onClose={() => setListing(false)}
          size="lg"
          title="List a property"
          description="What the land registry says, and what the business is asking for it."
          footer={
            <>
              <Button variant="outline" onClick={() => setListing(false)}>Cancel</Button>
              <Button
                loading={createPropertyState.isLoading}
                disabled={!propertyIsComplete(propertyForm)}
                onClick={() => void submitProperty()}
              >
                List it
              </Button>
            </>
          }
        >
          <PropertyFields value={propertyForm} onChange={setPropertyForm} />
        </Modal>
      )}

      {selling && (
        <Modal
          open
          onClose={() => setSelling(false)}
          size="lg"
          title="New property sale"
          description="The property is reserved until the last shilling is in."
          footer={
            <>
              <Button variant="outline" onClick={() => setSelling(false)}>Cancel</Button>
              <Button
                loading={createSaleState.isLoading}
                disabled={!saleForm.propertyId || !saleForm.customerId || !saleForm.price || !saleForm.accountId}
                onClick={() => void submitSale()}
              >
                Agree the sale
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Select
              label="Property"
              required
              value={saleForm.propertyId}
              onChange={(event) => {
                const property = available.data?.items.find((item) => item.id === event.target.value);
                setSaleForm({
                  ...saleForm,
                  propertyId: event.target.value,
                  // The asking price is the starting point; land is haggled.
                  price: property?.askingPrice ?? saleForm.price,
                });
              }}
            >
              <option value="">Select a property</option>
              {available.data?.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.reference} — {item.name}
                </option>
              ))}
            </Select>

            {selectedProperty && (
              <Alert tone="info">
                {[
                  selectedProperty.titleNumber && `Title ${selectedProperty.titleNumber}`,
                  selectedProperty.area &&
                    `${selectedProperty.area} ${selectedProperty.areaUnit.toLowerCase().replace('_', ' ')}`,
                  [selectedProperty.location, selectedProperty.district].filter(Boolean).join(', '),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                {' · asking '}
                {formatMoney(selectedProperty.askingPrice, format)}
              </Alert>
            )}

            <CustomerPicker
              required
              label="Buyer"
              placeholder="Select a buyer"
              value={saleForm.customerId}
              onChange={(customerId) => setSaleForm({ ...saleForm, customerId })}
              hint="Land is never a walk-in sale — the buyer goes on the title."
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Agreed price"
                type="number"
                required
                min="0"
                step="0.01"
                value={saleForm.price}
                onChange={(event) => setSaleForm({ ...saleForm, price: event.target.value })}
              />
              <Select
                label="Payment plan"
                value={saleForm.paymentPlan}
                onChange={(event) => setSaleForm({ ...saleForm, paymentPlan: event.target.value })}
              >
                <option value="INSTALMENTS">Instalments</option>
                <option value="OUTRIGHT">Paid outright</option>
              </Select>

              {saleForm.paymentPlan === 'INSTALMENTS' && (
                <Input
                  label="First instalment due"
                  type="date"
                  value={saleForm.nextDueAt}
                  onChange={(event) => setSaleForm({ ...saleForm, nextDueAt: event.target.value })}
                  hint="The business is emailed as the date approaches, and again if it passes."
                />
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Input
                label="Deposit taken now"
                type="number"
                min="0"
                step="0.01"
                value={saleForm.depositAmount}
                onChange={(event) => setSaleForm({ ...saleForm, depositAmount: event.target.value })}
                hint="Leave blank if nothing changed hands yet."
              />
              <Select
                label="Method"
                value={saleForm.methodId}
                onChange={(event) => setSaleForm({ ...saleForm, methodId: event.target.value })}
              >
                <option value="">Cash</option>
                {methods?.map((method) => (
                  <option key={method.id} value={method.id}>{method.name}</option>
                ))}
              </Select>
              <Select
                label="Into account"
                required
                value={saleForm.accountId}
                onChange={(event) => setSaleForm({ ...saleForm, accountId: event.target.value })}
                hint="Where every payment for this sale lands."
              >
                <option value="">Select an account</option>
                {accounts?.items
                  .filter((account) => account.isActive)
                  .map((account) => (
                    <option key={account.id} value={account.id}>{account.name}</option>
                  ))}
              </Select>
            </div>

            <Textarea
              label="Notes"
              rows={2}
              value={saleForm.notes}
              onChange={(event) => setSaleForm({ ...saleForm, notes: event.target.value })}
            />
          </div>
        </Modal>
      )}

      {paying && (
        <Modal
          open
          onClose={() => setPaying(null)}
          title={`Payment against ${paying.number}`}
          description={`${paying.customer.name} owes ${formatMoney(paying.dueAmount, format)}.`}
          footer={
            <>
              <Button variant="outline" onClick={() => setPaying(null)}>Cancel</Button>
              <Button
                loading={paymentState.isLoading}
                disabled={!(Number(payment.amount) > 0)}
                onClick={() => void submitPayment()}
              >
                Record payment
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Input
              label="Amount"
              type="number"
              min="0"
              step="0.01"
              value={payment.amount}
              onChange={(event) => setPayment({ ...payment, amount: event.target.value })}
              hint="Part of the balance is the normal case on land."
            />
            <Select
              label="Method"
              value={payment.methodId}
              onChange={(event) => setPayment({ ...payment, methodId: event.target.value })}
            >
              <option value="">Cash</option>
              {methods?.map((method) => (
                <option key={method.id} value={method.id}>{method.name}</option>
              ))}
            </Select>
            <Select
              label="Into account"
              value={payment.accountId}
              onChange={(event) => setPayment({ ...payment, accountId: event.target.value })}
              hint={paying.account ? `Blank uses ${paying.account.name}.` : undefined}
            >
              <option value="">{paying.account?.name ?? 'Select an account'}</option>
              {accounts?.items
                .filter((account) => account.isActive)
                .map((account) => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
            </Select>
            <Input
              label="Reference"
              value={payment.reference}
              onChange={(event) => setPayment({ ...payment, reference: event.target.value })}
            />
            <Input
              label="Next instalment due"
              type="date"
              value={payment.nextDueAt}
              onChange={(event) => setPayment({ ...payment, nextDueAt: event.target.value })}
              hint={
                paying.nextDueAt
                  ? `Leave it to keep ${formatDate(paying.nextDueAt, format)}. A sale paid in full needs no date.`
                  : 'Set a date and the business is reminded before it passes.'
              }
            />
          </div>
        </Modal>
      )}

      {cancelling && (
        <Modal
          open
          onClose={() => setCancelling(null)}
          size="sm"
          title={`Cancel ${cancelling.number}?`}
          footer={
            <>
              <Button variant="outline" onClick={() => setCancelling(null)}>Keep it</Button>
              <Button
                variant="danger"
                loading={cancelState.isLoading}
                disabled={!reason}
                onClick={() => void submitCancel()}
              >
                Cancel the sale
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Alert tone="warning">
              The property becomes available again. Money already taken is <strong>not</strong>{' '}
              refunded — it stays on the record, because a cancelled sale must not quietly erase
              money the business received. Refund it separately if that is what was agreed.
            </Alert>
            <Input
              label="Reason"
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </Modal>
      )}

      {hiding && (
        <Modal
          open
          onClose={() => setHiding(null)}
          size="sm"
          title={`Delete ${hiding.number}?`}
          footer={
            <>
              <Button variant="outline" onClick={() => setHiding(null)}>Cancel</Button>
              <Button variant="danger" loading={hideState.isLoading} onClick={() => void confirmHide()}>
                Delete it
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-secondary">
              The sale comes off the list and{' '}
              <span className="font-medium">
                {formatMoney(hiding.paidAmount, format)} goes back out of the cash account
              </span>{' '}
              as a dated reversal. {hiding.property.reference} returns to the market. Nothing is
              destroyed — anyone who may delete a sale can still see this one and put it back.
            </p>
            <p className="text-sm text-muted">
              A deal that fell through is a cancellation, not a deletion: cancelling keeps the
              money on the record as something to refund.
            </p>
            <Input
              label="Reason"
              placeholder="Recorded twice"
              value={hideReason}
              onChange={(event) => setHideReason(event.target.value)}
            />
          </div>
        </Modal>
      )}

      {editingSale && (
        <PropertySaleEditModal
          sale={editingSale}
          onClose={() => setEditingSale(null)}
          onSaved={() => setNotice('The agreement has been updated.')}
        />
      )}

      {viewing && (
        <PropertyDetailModal propertyId={viewing} onClose={() => setViewing(null)} />
      )}

      {printing && (
        <PropertySaleReceipt saleId={printing.id} onClose={() => setPrinting(null)} />
      )}
    </>
  );
}

/**
 * When the next instalment is expected, and whether that has passed.
 *
 * A date on its own tells you nothing at a glance — what a clerk needs to see
 * down a column is which rows are late.
 */
function DueDate({ value, settled }: { value: string | null; settled: boolean }) {
  const format = useFormat();
  if (settled || !value) return <span className="text-muted">—</span>;

  const due = new Date(value);
  const today = new Date();
  const days = Math.ceil((due.getTime() - today.getTime()) / 86_400_000);

  if (days < 0) {
    return (
      <span className="text-sm font-medium text-red-600 dark:text-red-400">
        {formatDate(value, format)}
        <span className="block text-xs font-normal">
          {Math.abs(days)} day{Math.abs(days) === 1 ? '' : 's'} late
        </span>
      </span>
    );
  }

  return (
    <span className={days <= 3 ? 'text-sm text-amber-600 dark:text-amber-400' : 'text-sm text-secondary'}>
      {formatDate(value, format)}
      {days <= 3 && (
        <span className="block text-xs">{days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`}</span>
      )}
    </span>
  );
}
